<template>
  <ValidationObserver
    ref="form"
    v-slot="{ handleSubmit }"
    slim
  >
    <form
      novalidate
      class="erpc-settings-form"
      :class="{'table-view': isTableView}"
      @submit.prevent="handleSubmit(onSubmit)"
    >
      <div class="row">
        <Field
          :label="$t('LABEL_NAME')"
          :is-required="true"
          class="col-xs-6"
        >
          <template #input>
            <ValidationProvider
              v-slot="{ classes }"
              ref="prConfigName"
              rules="required"
              slim
            >
              <input
                v-model="predictiveRoutingConfig.name"
                type="text"
                name="prConfigName"
                :class="classes"
                class="form-control"
              />
              <span
                v-t="'REQUIRED'"
                class="r-form__input-error"
              />
            </ValidationProvider>
          </template>
        </Field>
        <div class="col-xs-6 display-flex">
          <!-- <Field
            class="m-r-10"
            :label="$t('CALL_COUNT')"
            :has-tooltip="true"
            :tooltip-text="$t('CALL_COUNT_TT')"
          >
            <template #input>
              <input
                v-model="callCount"
                type="number"
                class="form-control"
              />
            </template>
          </Field> -->
          <Field
            class="m-r-10"
            :label="$t('USE_REVENUE')"
            :input-classes="'display-flex flex--align-center'"
            :has-tooltip="true"
            :tooltip-text="$t('USE_REVENUE_TT')"
          >
            <template #input>
              <nzToggle
                v-model="useRevenue"
                :disabled="isConverting"
                @onToggle="toggleUseRevenue"
              />
            </template>
          </Field>
          <Field
            v-if="!useRevenue"
            :label="$t('ADVANCED_TEXT')"
            :input-classes="'display-flex flex--align-center'"
          >
            <template #input>
              <nzToggle
                v-model="advancedConfig"
                :disabled="isConverting"
                @onToggle="toggleAdvancedConfig"
              />
              <Loader
                v-if="isConverting"
                custom-class="size-sm m-l-5"
              />
            </template>
          </Field>
        </div>
      </div>
      <div v-if="!useRevenue">
        <transition name="fade">
          <ERPCSettingsAdvanced
            v-if="showAdvancedSettings"
            :erpc-setting.sync="predictiveRoutingConfig.setting.erpcSetting"
          />
        </transition>
        <transition name="fade">
          <ERPCSettingsBasic
            v-if="showBasicSettings"
            :erpc-setting-basic.sync="predictiveRoutingConfig.setting.erpcSettingBasic"
          />
        </transition>
      </div>
      <div
        v-if="showFormButtons"
        class="row display-flex m-15"
        :class="{'flex--justify-end': !isTableView}"
      >
        <PrimaryButton
          size="sm"
          :show-loader-on-form="showLoader"
        >
          <span v-t="isNewConfig ? 'CREATE' : 'UPDATE'" />
        </PrimaryButton>

        <button
          v-t="'CANCEL'"
          class="btn btn-sm btn-danger m-l-15"
          @click="onCancel"
        />
      </div>
    </form>
  </ValidationObserver>
</template>

<script>
import { cloneDeep } from 'lodash';
import { mapState, mapActions, mapGetters } from 'vuex';
import { STATUS_PENDING, STATUS_ERROR } from '@/store/helpers';
import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import messages from '../locales';
import sharedMessages from '@/core/locales';
import Field from '@/core/components/Form/BaseFormFieldV2';
import nzToggle from '@/core/components/nzToggle/nzToggle';
import Loader from '@/core/components/Loader';
import ERPCSettingsBasic from './ERPCSettingsBasic';
import ERPCSettingsAdvanced from './ERPCSettingsAdvanced';
import { defaultERPCSettingBasic, defaultERPCSetting } from '../helpers/constants';

export default {
  name: 'ERPCSettingsForm',
  components: {
    Field,
    nzToggle,
    Loader,
    ERPCSettingsBasic,
    ERPCSettingsAdvanced
  },
  props: {
    showLoader: {
      type: Boolean,
      default: () => false
    },
    currentPredictiveRoutingConfig: {
      type: Object,
      default: function() {
        return {
          name: '',
          setting: {
            useERPC: true,
            useBasicSetting: true,
            erpcSettingBasic: defaultERPCSettingBasic,
            erpcSetting: null,
          }
        };
      }
    },
    isNewConfig: {
      type: Boolean,
      default: true
    },
    isTableView: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      advancedConfig: false,
      useRevenue: false,
      isConverting: false,
      callCount: 100,
      predictiveRoutingConfig: cloneDeep(this.currentPredictiveRoutingConfig),
    }
  },
  created() {
    if (!this.predictiveRoutingConfig?.setting?.useBasicSetting) this.advancedConfig = true;
    if (!this.predictiveRoutingConfig?.setting?.useERPC) this.useRevenue = true;
  },
  computed: {
    ...mapState({
      flags: 'currentAccountFeatureFlags'
    }),
    ...mapGetters('predictiveRouting', [
      'convertPredictiveRoutingConfigToAdvancedStatus',
      'convertPredictiveRoutingConfigToBasicStatus',
    ]),
    showFormButtons() {
      return (this.advancedConfig && !this.predictiveRoutingConfig.setting.useBasicSetting) || (!this.advancedConfig && this.predictiveRoutingConfig.setting.useBasicSetting) ? true : false;
    },
    showBasicSettings() {
      return !this.advancedConfig && this.predictiveRoutingConfig.setting.useBasicSetting && !this.isConverting;
    },
    showAdvancedSettings() {
      return this.advancedConfig && !this.predictiveRoutingConfig.setting.useBasicSetting && !this.isConverting;
    },
    hasRpcLegacy() {
      return !!this.flags.rpc_legacy;
    },
  },
  methods: {
    ...mapActions('predictiveRouting', [
      'convertPredictiveRoutingConfigToAdvanced',
      'convertPredictiveRoutingConfigToBasic'
    ]),
    async toggleAdvancedConfig() {
      this.isConverting = true;
      this.advancedConfig = !this.advancedConfig;
      let payload = {};
      if (this.advancedConfig) {
        // Convert basic settings to advanced
        payload = {
          config: this.predictiveRoutingConfig.setting.erpcSettingBasic || defaultERPCSettingBasic,
          callCount: this.callCount
        }

        const response = await this.convertPredictiveRoutingConfigToAdvanced(payload);

        if (this.convertPredictiveRoutingConfigToAdvancedStatus.status === STATUS_ERROR) {
          this.advancedConfig = false;
          this.isConverting = false;
          utilities.showToaster(STATUS_ERROR, this.convertPredictiveRoutingConfigToAdvancedStatus.error);
          return;
        }

        this.predictiveRoutingConfig.setting.erpcSetting = response;
        this.predictiveRoutingConfig.setting.erpcSettingBasic = null;
        this.predictiveRoutingConfig.setting.useERPC = true;
        this.predictiveRoutingConfig.setting.useBasicSetting = false;

        this.isConverting = false;
      } else {
        // Convert advanced settings to basic
        payload = {
          config: this.predictiveRoutingConfig.setting.erpcSetting || defaultERPCSetting,
          callCount: this.callCount
        }

        const response = await this.convertPredictiveRoutingConfigToBasic(payload);

        if (this.convertPredictiveRoutingConfigToBasicStatus.status === STATUS_ERROR) {
          this.advancedConfig = true;
          this.isConverting = false;
          utilities.showToaster(STATUS_ERROR, this.convertPredictiveRoutingConfigToBasicStatus.error);
          return;
        }

        this.predictiveRoutingConfig.setting.erpcSettingBasic = response;
        this.predictiveRoutingConfig.setting.erpcSetting = null;
        this.predictiveRoutingConfig.setting.useERPC = true;
        this.predictiveRoutingConfig.setting.useBasicSetting = true;

        this.isConverting = false;
      }
    },
    toggleUseRevenue() {
      this.useRevenue = !this.useRevenue;
      this.predictiveRoutingConfig.setting.useERPC = this.useRevenue ? false : true;
    },
    onSubmit() {
      // If the account does not have the legacy RPC settings, remove those props from the payload
      if (!this.hasRpcLegacy) {
        delete this.predictiveRoutingConfig.setting.erpcSetting?.defaultRatio;
        delete this.predictiveRoutingConfig.setting.erpcSetting?.overrideRatio;
        delete this.predictiveRoutingConfig.setting.erpcSetting?.overrideAmount;
      }
      this.$emit('submit', this.predictiveRoutingConfig);
    },
    onCancel() {
      this.$emit('cancel', this.predictiveRoutingConfig);
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>